import { useQuery } from "@tanstack/react-query";
import type React from "react";

import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { CommunityPoll } from "@/components/CommunityFeed/CommunityPoll";
import { ALL_COMMUNITY_FEED_ITEM_TYPES } from "@/components/CommunityFeed/helpers";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { canListMessages } from "@/helpers/permissions";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const AMOUNT_MAX_POST = 5;

export function HomeCommunityPostList(): React.ReactNode {
  const hasPermission = usePermission();
  const slug = useSlug();
  const { t } = useTranslation();

  const communityFeedQueries = useCommunityFeedQueries();
  const {
    data: postsData,
    isPending: isPendingPosts,
    error: errorPosts,
  } = useQuery({
    ...communityFeedQueries.listPosts({
      Tab: "all",
      Offset: 0,
      Limit: AMOUNT_MAX_POST,
      Connection: "included",
      FeedTypes: ALL_COMMUNITY_FEED_ITEM_TYPES,
      ExcludeArchivedPosts: true,
    }),
    enabled: hasPermission(canListMessages),
  });

  if (isPendingPosts) {
    return <FullSizeLoader withPadding />;
  }

  if (errorPosts) {
    return <ErrorPage error={errorPosts} />;
  }

  const posts = postsData.items;

  if (posts.length === 0) {
    return null;
  }

  return (
    <section className="flex w-full grow flex-col gap-space-md">
      <Spread space="sm">
        <h2 className="text-heading-2">{t("page.home.message-feed", { count: posts.length })}</h2>
        <Button type="link" variant="ghostPrimary" href={routes.communityFeed.list({ slug })}>
          {t("page.home.message-feed-link")}
        </Button>
      </Spread>
      <CommunityPostViewCountTrackingProvider>
        <Stack gap="lg">
          {posts.map((post) => {
            if (post.message) {
              return <CommunityMessage key={`message-${post.message.id}`} message={post.message} />;
            }

            if (post.poll) {
              return <CommunityPoll key={`poll-${post.poll.id}`} poll={post.poll} />;
            }

            return null;
          })}
        </Stack>
      </CommunityPostViewCountTrackingProvider>
    </section>
  );
}
